import React, { useState } from 'react';
import {
    MarkdownEditor,
    CardRow,
    CardSection,
    SimpleCard,
    ValidatingInput,
    Select,
    Tooltip,
} from '@ygtechnologies/bygr-frontend-component-library';
import theme from './BuyerOptionPresetGroupInfo.module.scss';
import { OptionPresetGroupDto, OptionPresetGroupType } from '@/services/option-preset/option-preset-group';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button, ButtonBar, Checkbox } from '@ygtechnologies/rubus-ui';
import { useRooms } from '@/services/rooms';
import { getRoomDisplayName } from '@/utils/rooms';
import { ProjectDeadlineSelect } from '../../project/ProjectSettings/ProjectDeadlineSelect';
import { BuyerOptionPresetStatus } from '@/types';

export interface BuyerOptionPresetGroupInfoEditProps {
    group: OptionPresetGroupDto;
    onChange(newValue: OptionPresetGroupDto): void;
    onSave(): void;
    onCancel(): void;
}

export const BuyerOptionPresetGroupInfoEdit: React.FC<BuyerOptionPresetGroupInfoEditProps> = ({
    group,
    onChange,
    onSave,
    onCancel,
}) => {
    const roomQuery = useRooms(group.projectId);
    const [isLoading, setIsLoading] = useState(false);
    const isPublished = group.status === BuyerOptionPresetStatus.Published;
    const isDeadlineLocked = isPublished || group.hasConnectedOptions;
    const deadlineLockReason = isPublished
        ? t`Unpublish this style group to change deadline`
        : t`Disconnect connected options to change deadline`;

    const handleChange = (changes: Partial<OptionPresetGroupDto>) => {
        onChange({ ...group, ...changes });
    };

    return (
        <SimpleCard className={theme.card}>
            <CardRow>
                <CardSection width="25%" caption={<Trans>Name</Trans>}>
                    <ValidatingInput
                        className="form-control"
                        value={group.name || ''}
                        onChange={(e) => handleChange({ name: e.target.value })}
                        placeholder={t`Group name`}
                        required
                    />
                </CardSection>
                <CardSection caption={<Trans>Type</Trans>}>
                    {group.type === OptionPresetGroupType.Room ? <Trans>Room</Trans> : <Trans>Main</Trans>}
                </CardSection>
            </CardRow>

            {/* Room Selection - only for Room type (read-only for now) */}
            {group.type === OptionPresetGroupType.Room && (
                <CardRow>
                    <CardSection caption={<Trans>Rooms</Trans>}>
                        <Select
                            isMulti
                            onChange={(value: any) => handleChange({ roomIds: value.map((v: any) => v.value) })}
                            value={
                                group.roomIds?.map((id) => {
                                    const r = roomQuery.data?.find((x) => x.id === id);
                                    return {
                                        value: id,
                                        label: r ? getRoomDisplayName(r.name, r.labelNote) : '',
                                    };
                                }) || []
                            }
                            options={
                                roomQuery.data?.map((x) => ({
                                    value: x.id,
                                    label: getRoomDisplayName(x.name, x.labelNote),
                                })) || []
                            }
                        />
                    </CardSection>
                </CardRow>
            )}

            <CardRow>
                <CardSection caption={<Trans>Deadline</Trans>}>
                    {isDeadlineLocked ? (
                        <Tooltip content={deadlineLockReason}>
                            <div>
                                <ProjectDeadlineSelect
                                    value={group.projectDeadlineId}
                                    onChange={(deadlineId) => handleChange({ projectDeadlineId: deadlineId })}
                                    projectId={group.projectId}
                                    required
                                    isDisabled
                                />
                            </div>
                        </Tooltip>
                    ) : (
                        <ProjectDeadlineSelect
                            value={group.projectDeadlineId}
                            onChange={(deadlineId) => handleChange({ projectDeadlineId: deadlineId })}
                            projectId={group.projectId}
                            required
                        />
                    )}
                </CardSection>
            </CardRow>

            {/* Description Section */}
            <CardRow>
                <div style={{ width: '100%' }}>
                    <CardSection caption={<Trans>Description</Trans>}>
                        <MarkdownEditor
                            markdown={group.description || ''}
                            onChange={(value) => handleChange({ description: value })}
                            minHeight="300px"
                        />
                    </CardSection>
                </div>
            </CardRow>

            {/* Settings Section */}
            <CardRow>
                <CardSection caption={<Trans>Settings</Trans>}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Checkbox
                            inputSize="md"
                            checked={group.forceSelection || false}
                            onChange={(e) => handleChange({ forceSelection: e.target.checked })}>
                            <Trans>Force buyer to choose style</Trans>
                        </Checkbox>

                        <Checkbox
                            inputSize="md"
                            checked={group.styleOptionsFree || false}
                            onChange={(e) => handleChange({ styleOptionsFree: e.target.checked })}>
                            <Trans>Default style option free</Trans>
                        </Checkbox>

                        <Checkbox
                            inputSize="md"
                            checked={group.legacyMode || false}
                            onChange={(e) => handleChange({ legacyMode: e.target.checked })}>
                            <Trans>Allow picking options outside style items</Trans>
                        </Checkbox>
                    </div>
                </CardSection>
            </CardRow>
            <CardRow>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={onCancel}>
                            <Trans>Cancel</Trans>
                        </Button>

                        <Button
                            loading={isLoading}
                            variant="primary"
                            onClick={() => {
                                setIsLoading(true);
                                onSave();
                            }}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </CardRow>
        </SimpleCard>
    );
};
